import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Backdrop,
  Slide,
  Fade,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  Tabs,
  Tab,
  RadioGroup,
  Radio
} from '@mui/material';
import { styled } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DevicesIcon from '@mui/icons-material/Devices';
import LanguageIcon from '@mui/icons-material/Language';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WifiIcon from '@mui/icons-material/Wifi';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SendIcon from '@mui/icons-material/Send';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import BugReportIcon from '@mui/icons-material/BugReport';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';
import ScreenshotIcon from '@mui/icons-material/Screenshot';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import LinkIcon from '@mui/icons-material/Link';
import HelpIcon from '@mui/icons-material/Help';
import HomeIcon from '@mui/icons-material/Home';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InventoryIcon from '@mui/icons-material/Inventory';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DetailsIcon from '@mui/icons-material/Details';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SellIcon from '@mui/icons-material/Sell';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

// Lista de países agrupados por idioma
const englishSpeakingCountries = ['US', 'CA', 'AU', 'GB', 'NZ', 'IE'];
const spanishSpeakingCountries = [
  'MX', 'CL', 'AR', 'UY', 'BO', 'CO', 'CR', 'CU', 'DO', 'EC',
  'ES', 'GT', 'GQ', 'HN', 'NI', 'PA', 'PY', 'PE', 'PR', 'SV', 'VE'
];
const portugueseSpeakingCountries = ['BR', 'PT'];

// Opções de destino da notificação
const destinationScreens = [
  { category: 'Telas Principais', options: [
    { value: 'home', label: 'Tela Inicial', icon: <HomeIcon />, needsId: false },
    { value: 'apiary', label: 'Apiário', icon: <HomeIcon />, needsId: false },
    { value: 'statistics', label: 'Estatísticas', icon: <AssessmentIcon />, needsId: false },
    { value: 'inventory', label: 'Inventário', icon: <InventoryIcon />, needsId: false },
    { value: 'qrcode', label: 'QR Code', icon: <QrCodeIcon />, needsId: false },
    { value: 'profile', label: 'Perfil', icon: <PersonIcon />, needsId: false },
  ]},
  { category: 'Enxames', options: [
    { value: 'swarmDetails', label: 'Detalhes do Enxame', icon: <DetailsIcon />, needsId: true },
    { value: 'editSwarm', label: 'Editar Enxame', icon: <EditIcon />, needsId: true },
    { value: 'registerSwarm', label: 'Registrar Enxame', icon: <AddCircleIcon />, needsId: false },
  ]},
  { category: 'Iscas', options: [
    { value: 'baitDetails', label: 'Detalhes da Isca', icon: <DetailsIcon />, needsId: true },
    { value: 'editBait', label: 'Editar Isca', icon: <EditIcon />, needsId: true },
    { value: 'registerBait', label: 'Registrar Isca', icon: <AddCircleIcon />, needsId: false },
  ]},
  { category: 'Operações', options: [
    { value: 'donation', label: 'Doação', icon: <CardGiftcardIcon />, needsId: false },
    { value: 'sale', label: 'Venda', icon: <SellIcon />, needsId: false },
    { value: 'loss', label: 'Perda', icon: <RemoveCircleIcon />, needsId: false },
    { value: 'transfer', label: 'Transferência', icon: <SwapHorizIcon />, needsId: false },
  ]},
  { category: 'Pagamento', options: [
    { value: 'payment', label: 'Pagamento/Assinatura', icon: <MonetizationOnIcon />, needsId: false },
  ]},
];

// Componentes estilizados
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 20px rgba(0,0,0,0.15)',
  },
}));

const PhoneContainer = styled(Paper)(({ theme }) => ({
  width: 280,
  height: 500,
  margin: '0 auto',
  borderRadius: 24,
  overflow: 'hidden',
  position: 'relative',
  border: '8px solid #111',
  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
}));

const PhoneStatusBar = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(0,0,0,0.8)',
  height: 30,
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 12px',
  color: 'white',
}));

const PhoneScreen = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  height: 'calc(100% - 30px)',
  overflow: 'auto',
  position: 'relative',
}));

const NotificationShade = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(0,0,0,0.8)',
  padding: '8px 0',
  width: '100%',
}));

const NotificationItem = styled(Paper)(({ theme }) => ({
  backgroundColor: 'white',
  margin: '8px 16px',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
}));

const NotificationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  backgroundColor: '#f8f8f8',
  borderBottom: '1px solid #eee',
}));

const NotificationContent = styled(Box)(({ theme }) => ({
  padding: '8px 12px',
}));

const SendButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
  color: 'white',
  padding: '12px 24px',
  transition: 'all 0.3s',
  '&:hover': {
    background: 'linear-gradient(45deg, #1976D2 30%, #00B0FF 90%)',
    transform: 'scale(1.02)',
  },
}));

const SuccessBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: '#fff',
  flexDirection: 'column',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
}));

const UserChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    transform: 'scale(1.05)',
  },
}));

const HeaderDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(2, 0),
  '&::before, &::after': {
    borderColor: theme.palette.primary.light,
  },
}));

const InfoPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.info.light,
  color: theme.palette.info.contrastText,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 8
}));

const WarningPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.warning.light,
  color: theme.palette.warning.contrastText,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 8
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  width: '100%',
  '& .MuiToggleButtonGroup-grouped': {
    flex: 1,
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius,
      marginLeft: 8,
      border: `1px solid ${theme.palette.divider}`,
    },
    '&:first-of-type': {
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`,
    },
  },
}));

const DestinationScreenCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'all 0.2s',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateX(5px)',
  },
  '&.selected': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    transform: 'translateX(10px)',
  }
}));

const SendNotification: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [additionalData, setAdditionalData] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [specificUsers, setSpecificUsers] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userSearchDialog, setUserSearchDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(true);
  const [sendMode, setSendMode] = useState<'filtered' | 'specific'>('filtered');
  const [selectedDestinationTab, setSelectedDestinationTab] = useState(0);
  const [selectedScreen, setSelectedScreen] = useState<string | null>(null);
  const [itemId, setItemId] = useState<string>('');
  const [destinationType, setDestinationType] = useState<'screen' | 'custom'>('screen');

  // Estados para os filtros
  const [languageFilter, setLanguageFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [filteredCountries, setFilteredCountries] = useState<string[]>([]);
  const [platformFilter, setPlatformFilter] = useState('ALL');
  const [planTypeFilter, setPlanTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Referência para rolagem automática
  const successRef = useRef<HTMLDivElement>(null);

  // Determinar se é necessário ID para a tela selecionada
  const needsId = () => {
    if (!selectedScreen) return false;
    
    for (const category of destinationScreens) {
      for (const option of category.options) {
        if (option.value === selectedScreen) {
          return option.needsId;
        }
      }
    }
    return false;
  };

  // Gerar JSON para dados adicionais baseado na tela selecionada
  const generateAdditionalData = () => {
    if (destinationType === 'custom') {
      return additionalData;
    }

    if (!selectedScreen) {
      return '{}';
    }

    let data: any = { screen: selectedScreen };
    
    if (needsId() && itemId) {
      data.id = itemId;
    }
    
    return JSON.stringify(data, null, 2);
  };

  // Atualizar o campo de dados adicionais quando a seleção de tela muda
  useEffect(() => {
    if (destinationType === 'screen') {
      setAdditionalData(generateAdditionalData());
    }
  }, [selectedScreen, itemId, destinationType]);

  // Ao selecionar o idioma, define os países correspondentes
  const handleLanguageChange = (language: string) => {
    setLanguageFilter(language);
    setCountryFilter('ALL'); // Reseta o filtro de país ao trocar o idioma

    if (language === 'en') {
      setFilteredCountries(englishSpeakingCountries);
    } else if (language === 'es') {
      setFilteredCountries(spanishSpeakingCountries);
    } else if (language === 'pt') {
      setFilteredCountries(portugueseSpeakingCountries);
    } else {
      setFilteredCountries([]);
    }
  };

  // Função que busca todos os usuários
  const fetchAllUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/users/all');
      setAllUsers(response.data);
      filterUsers(response.data);
    } catch (err) {
      setError('Falha ao carregar usuários. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Função para filtrar usuários com base nos filtros selecionados
  const filterUsers = (users = allUsers) => {
    if (!users.length) return;

    const filtered = users.filter((user: any) => {
      // Sempre filtrar usuários sem tokens de dispositivo
      if (!user.deviceTokens || user.deviceTokens.length === 0) {
        return false;
      }

      // Filtrar por país específico
      if (countryFilter !== 'ALL' && user.countryCode !== countryFilter) {
        return false;
      }

      // Filtrar por idioma
      if (languageFilter) {
        if (languageFilter === 'en' && !englishSpeakingCountries.includes(user.countryCode)) {
          return false;
        } else if (languageFilter === 'es' && !spanishSpeakingCountries.includes(user.countryCode)) {
          return false;
        } else if (languageFilter === 'pt' && !portugueseSpeakingCountries.includes(user.countryCode)) {
          return false;
        }
      }

      // Filtrar por plataforma
      if (platformFilter !== 'ALL' && user.platformType !== platformFilter) {
        return false;
      }

      // Filtrar por tipo de plano
      if (planTypeFilter !== 'ALL' && user.planType !== planTypeFilter) {
        return false;
      }

      // Filtrar por status
      if (statusFilter !== 'ALL' && user.status !== statusFilter) {
        return false;
      }

      return true;
    });

    setFilteredUsers(filtered);
  };

  // Função para adicionar um usuário específico
  const addSpecificUser = (user: any) => {
    if (!specificUsers.some(u => u.id === user.id)) {
      setSpecificUsers([...specificUsers, user]);
    }
  };

  // Função para remover um usuário específico
  const removeSpecificUser = (userId: number) => {
    setSpecificUsers(specificUsers.filter(u => u.id !== userId));
  };

  // Função para limpar todos os usuários específicos
  const clearSpecificUsers = () => {
    setSpecificUsers([]);
  };

  // Função para filtrar a lista de usuários na busca
  const filteredSearchUsers = allUsers.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Função para selecionar apenas o usuário atual (para teste)
  const selectOnlyMe = () => {
    const currentUser = allUsers.find(u => u.email === user?.email);
    if (currentUser && currentUser.deviceTokens && currentUser.deviceTokens.length > 0) {
      setSpecificUsers([currentUser]);
      setSendMode('specific');
    } else {
      setError('Seu usuário não tem dispositivo registrado para receber notificações.');
    }
  };

  // Função para verificar se um usuário tem dispositivo registrado
  const hasDeviceToken = (user: any) => {
    return user.deviceTokens && user.deviceTokens.length > 0;
  };

  // Calcular os usuários que vão receber a notificação com base no modo selecionado
  const getRecipientsCount = () => {
    if (sendMode === 'filtered') {
      return filteredUsers.length;
    } else {
      // Apenas usuários específicos que têm device tokens
      return specificUsers.filter(user => hasDeviceToken(user)).length;
    }
  };

  // Função para enviar notificações para os usuários selecionados
  const handleSendNotification = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!title.trim() || !body.trim()) {
      setError('Título e corpo da notificação são obrigatórios.');
      setLoading(false);
      return;
    }

    try {
      let dataObj = {};
      
      // Tenta fazer o parse do JSON
      try {
        const jsonData = destinationType === 'screen' 
          ? generateAdditionalData() 
          : additionalData;
          
        if (jsonData && jsonData.trim()) {
          dataObj = JSON.parse(jsonData);
        }
      } catch (err) {
        setError('O campo de dados adicionais deve ser um JSON válido. Exemplo: {"key": "value"}');
        setLoading(false);
        return;
      }

      // Determina quais usuários receberão a notificação com base no modo
      let recipientUsers = [];
      if (sendMode === 'filtered') {
        recipientUsers = filteredUsers;
      } else {
        // Apenas usuários específicos com tokens
        recipientUsers = specificUsers.filter(user => hasDeviceToken(user));
      }

      if (recipientUsers.length === 0) {
        setError('Nenhum usuário selecionado tem dispositivo registrado para receber notificações.');
        setLoading(false);
        return;
      }

      const userIds = recipientUsers.map(user => user.id);
      
      const response = await api.post('/notifications/send-filtered', {
        title,
        body,
        data: dataObj,
        userIds
      });

      setSuccess(`Notificação enviada com sucesso para ${userIds.length} usuário(s)!`);
      setShowSuccess(true);
      
      // Rola a tela para mostrar a mensagem de sucesso
      setTimeout(() => {
        successRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
      // Esconde a mensagem de sucesso após 3 segundos
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
      
    } catch (err) {
      setError('Falha ao enviar notificação. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Limpar o formulário
  const handleClearForm = () => {
    setTitle('');
    setBody('');
    if (destinationType === 'custom') {
      setAdditionalData('');
    } else {
      setSelectedScreen(null);
      setItemId('');
    }
  };

  // useEffect para carregar os usuários automaticamente se o usuário for administrador
  useEffect(() => {
    if (user && ['kennyldoofc@gmail.com', 'kennildodev@gmail.com'].includes(user.email)) {
      fetchAllUsers();
    }
  }, [user]);

  // useEffect para aplicar os filtros quando eles são alterados
  useEffect(() => {
    filterUsers();
  }, [languageFilter, countryFilter, platformFilter, planTypeFilter, statusFilter]);

  // Obter a hora atual formatada para a visualização do telefone
  const getCurrentTime = () => {
    const now = new Date();
    return now.getHours().toString().padStart(2, '0') + ':' + 
           now.getMinutes().toString().padStart(2, '0');
  };

  // Renderiza a visualização prévia da notificação em formato de celular
  const renderPhonePreview = () => {
    return (
      <PhoneContainer>
        <PhoneStatusBar>
          <Typography variant="caption" fontWeight="bold">
            {getCurrentTime()}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <WifiIcon fontSize="small" />
            <SignalCellularAltIcon fontSize="small" />
            <BatteryFullIcon fontSize="small" />
          </Box>
        </PhoneStatusBar>
        <PhoneScreen>
          <NotificationShade>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'white', 
                display: 'block', 
                textAlign: 'center',
                mb: 1
              }}
            >
              Deslize para baixo para ver notificações
            </Typography>
          </NotificationShade>
          <NotificationItem>
            <NotificationHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                  <NotificationsIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="caption" fontWeight="bold">
                    {title || 'Título da Notificação'}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    Agora mesmo
                  </Typography>
                </Box>
                <IconButton size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </NotificationHeader>
            <NotificationContent>
              <Typography variant="body2">
                {body || 'Corpo da notificação com texto detalhado...'}
              </Typography>
            </NotificationContent>
          </NotificationItem>
          
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Dados adicionais (não visíveis para o usuário):
            </Typography>
            <Paper sx={{ p: 1, backgroundColor: '#f0f0f0', mt: 1, maxHeight: 80, overflow: 'auto' }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {destinationType === 'screen' ? generateAdditionalData() : additionalData || '{}'}
              </Typography>
            </Paper>
            
            {destinationType === 'screen' && selectedScreen && (
              <Box sx={{ mt: 2, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ScreenShareIcon fontSize="small" color="primary" />
                  Ao tocar nesta notificação, o usuário será direcionado para: 
                  <Box component="span" sx={{ fontWeight: 'bold', ml: 0.5 }}>
                    {destinationScreens.flatMap(cat => cat.options).find(opt => opt.value === selectedScreen)?.label}
                    {needsId() && itemId && ` (ID: ${itemId})`}
                  </Box>
                </Typography>
              </Box>
            )}
          </Box>
        </PhoneScreen>
      </PhoneContainer>
    );
  };

  // Dialog para busca de usuários específicos
  const renderUserSearchDialog = () => {
    return (
      <Dialog 
        open={userSearchDialog} 
        onClose={() => setUserSearchDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Selecionar Usuários Específicos</Typography>
            <IconButton onClick={() => setUserSearchDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Usuários sem dispositivos registrados aparecem em cinza e não receberão notificações mesmo se selecionados.
          </Alert>

          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <TextField
              label="Buscar por email ou nome"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <Button 
              variant="outlined"
              onClick={() => setSearchTerm('')}
              startIcon={<ClearIcon />}
            >
              Limpar
            </Button>
          </Box>
          
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>País</TableCell>
                  <TableCell>Plataforma</TableCell>
                  <TableCell>Plano</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSearchUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSearchUsers.map((user) => {
                    const isSelected = specificUsers.some(u => u.id === user.id);
                    const hasDevice = hasDeviceToken(user);
                    
                    return (
                      <TableRow 
                        key={user.id}
                        sx={{ 
                          backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.1)' : 'inherit',
                          opacity: hasDevice ? 1 : 0.6
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {user.email}
                            {!hasDevice && (
                              <Tooltip title="Este usuário não tem dispositivo registrado e não receberá notificações">
                                <Chip
                                  size="small"
                                  label="Sem dispositivo"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ ml: 1, height: 20 }}
                                  icon={<WarningIcon style={{ fontSize: 14 }} />}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{user.countryCode || '-'}</TableCell>
                        <TableCell>{user.platformType || '-'}</TableCell>
                        <TableCell>{user.planType || '-'}</TableCell>
                        <TableCell>{user.status || '-'}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant={isSelected ? "contained" : "outlined"}
                            color={isSelected ? "primary" : "inherit"}
                            size="small"
                            onClick={() => isSelected ? removeSpecificUser(user.id) : addSpecificUser(user)}
                            startIcon={isSelected ? <CheckCircleIcon /> : <PersonAddIcon />}
                          >
                            {isSelected ? 'Selecionado' : 'Selecionar'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={clearSpecificUsers} 
            color="error" 
            startIcon={<DeleteIcon />}
            disabled={specificUsers.length === 0}
          >
            Limpar Seleção ({specificUsers.length})
          </Button>
          <Button 
            onClick={() => setUserSearchDialog(false)} 
            variant="contained"
            color="primary"
          >
            Concluir
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Renderizar o seletor de tela de destino
  const renderDestinationSelector = () => {
    return (
      <StyledCard sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScreenShareIcon color="primary" />
            Destino da Notificação
          </Typography>
          
          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
            <RadioGroup
              row
              value={destinationType}
              onChange={(e) => setDestinationType(e.target.value as 'screen' | 'custom')}
            >
              <FormControlLabel value="screen" control={<Radio />} label="Selecionar Tela" />
              <FormControlLabel value="custom" control={<Radio />} label="JSON Personalizado" />
            </RadioGroup>
          </FormControl>

          {destinationType === 'screen' ? (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs 
                  value={selectedDestinationTab} 
                  onChange={(e, newValue) => setSelectedDestinationTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {destinationScreens.map((category, index) => (
                    <Tab key={index} label={category.category} />
                  ))}
                </Tabs>
              </Box>

              <Box sx={{ maxHeight: 230, overflow: 'auto', p: 1 }}>
                {destinationScreens[selectedDestinationTab].options.map((option, index) => (
                  <DestinationScreenCard 
                    key={index}
                    className={selectedScreen === option.value ? 'selected' : ''}
                    onClick={() => {
                      setSelectedScreen(option.value);
                      if (!option.needsId) {
                        setItemId('');
                      }
                    }}
                  >
                    {option.icon}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1">{option.label}</Typography>
                    </Box>
                    {selectedScreen === option.value && (
                      <CheckCircleIcon color="primary" />
                    )}
                  </DestinationScreenCard>
                ))}
              </Box>

              {needsId() && (
                <TextField
                  label="ID do Item"
                  value={itemId}
                  onChange={(e) => setItemId(e.target.value)}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  placeholder="Ex: 123"
                  required
                  helperText={`Digite o ID ${selectedScreen?.includes('Swarm') || selectedScreen?.includes('enxame') ? 'do enxame' : 'da isca'} para onde direcionar o usuário`}
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              )}

              {selectedScreen && (
                <Paper 
                  sx={{ 
                    p: 1.5, 
                    mt: 2, 
                    backgroundColor: '#f0f9ff', 
                    borderRadius: 2,
                    border: '1px solid #bbdefb'
                  }}
                >
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <InfoIcon color="primary" sx={{ mt: 0.5 }} />
                    <Box>
                      Ao tocar nesta notificação, o app abrirá diretamente na tela <strong>{destinationScreens.flatMap(cat => cat.options).find(opt => opt.value === selectedScreen)?.label}</strong>
                      {needsId() && itemId && <span>, mostrando o item com ID <strong>{itemId}</strong></span>}
                      {needsId() && !itemId && <span>. <strong>Atenção:</strong> É necessário fornecer um ID para esta tela.</span>}
                    </Box>
                  </Typography>
                </Paper>
              )}
            </>
          ) : (
            <TextField
              label="Dados Adicionais (JSON)"
              value={additionalData}
              onChange={(e) => setAdditionalData(e.target.value)}
              fullWidth
              multiline
              rows={6}
              margin="normal"
              variant="outlined"
              placeholder='{"screen": "baitDetails", "id": "123"}'
              helperText="Dados adicionais em formato JSON para ações específicas no app"
              InputProps={{
                sx: { borderRadius: 2, fontFamily: 'monospace' }
              }}
            />
          )}
          
          {destinationType === 'custom' && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Exemplos de destinos:
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Chip 
                    label='{"screen": "home"}'
                    variant="outlined"
                    size="small"
                    onClick={() => setAdditionalData('{"screen": "home"}')}
                    sx={{ m: 0.5 }}
                  />
                  <Chip 
                    label='{"screen": "swarmDetails", "id": "123"}'
                    variant="outlined"
                    size="small"
                    onClick={() => setAdditionalData('{"screen": "swarmDetails", "id": "123"}')}
                    sx={{ m: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Chip 
                    label='{"screen": "inventory"}'
                    variant="outlined"
                    size="small"
                    onClick={() => setAdditionalData('{"screen": "inventory"}')}
                    sx={{ m: 0.5 }}
                  />
                  <Chip 
                    label='{"screen": "payment"}'
                    variant="outlined"
                    size="small"
                    onClick={() => setAdditionalData('{"screen": "payment"}')}
                    sx={{ m: 0.5 }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </StyledCard>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Backdrop de sucesso */}
      <SuccessBackdrop open={showSuccess}>
        <Fade in={showSuccess}>
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Notificação Enviada!
            </Typography>
            <Typography>
              Sua mensagem foi entregue com sucesso.
            </Typography>
          </Box>
        </Fade>
      </SuccessBackdrop>
    
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold" sx={{ 
          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Enviar Notificações
        </Typography>
        <Box>
          <Tooltip title="Atualizar dados">
            <IconButton onClick={fetchAllUsers} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <HeaderDivider textAlign="center">
        <Chip 
          label="Gerenciador de Notificações Push" 
          color="primary" 
          icon={<NotificationsIcon />}
        />
      </HeaderDivider>

      {/* Modo de envio toggle */}
      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
          Modo de Envio:
        </Typography>
        <StyledToggleButtonGroup
          value={sendMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode) setSendMode(newMode);
          }}
          aria-label="modo de envio"
        >
          <ToggleButton value="filtered" aria-label="todos filtrados">
            <GroupIcon sx={{ mr: 1 }} />
            Todos Filtrados ({filteredUsers.length})
          </ToggleButton>
          <ToggleButton value="specific" aria-label="apenas selecionados">
            <PersonIcon sx={{ mr: 1 }} />
            Apenas Selecionados ({specificUsers.filter(hasDeviceToken).length})
          </ToggleButton>
        </StyledToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Slide direction="right" in={true} mountOnEnter unmountOnExit>
            <StyledCard sx={{ mb: 3 }}>
              <CardContent>
                {sendMode === 'filtered' ? (
                  <>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 2 
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <FilterListIcon color="primary" />
                        Filtros de Usuários
                      </Typography>
                      <Button 
                        variant="text" 
                        color="primary"
                        onClick={() => setShowFilterPanel(!showFilterPanel)}
                      >
                        {showFilterPanel ? 'Ocultar' : 'Mostrar'}
                      </Button>
                    </Box>
                    
                    {showFilterPanel && (
                      <>
                        <InfoPaper>
                          <InfoIcon />
                          <Typography variant="body2">
                            Apenas usuários com dispositivos registrados podem receber notificações push.
                          </Typography>
                        </InfoPaper>

                        {/* Filtro por idioma */}
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Idioma</InputLabel>
                          <Select
                            value={languageFilter}
                            onChange={(e) => handleLanguageChange(e.target.value)}
                            startAdornment={<LanguageIcon sx={{ mr: 1 }} />}
                          >
                            <MenuItem value="">Todos os Idiomas</MenuItem>
                            <MenuItem value="pt">Português</MenuItem>
                            <MenuItem value="es">Espanhol</MenuItem>
                            <MenuItem value="en">Inglês</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Filtro por país, baseado no idioma selecionado */}
                        {filteredCountries.length > 0 && (
                          <FormControl fullWidth margin="normal">
                            <InputLabel>País</InputLabel>
                            <Select
                              value={countryFilter}
                              onChange={(e) => setCountryFilter(e.target.value)}
                            >
                              <MenuItem value="ALL">Todos os Países</MenuItem>
                              {filteredCountries.map((countryCode) => (
                                <MenuItem key={countryCode} value={countryCode}>
                                  {countryCode}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {/* Filtro por plataforma */}
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Plataforma</InputLabel>
                          <Select
                            value={platformFilter}
                            onChange={(e) => setPlatformFilter(e.target.value)}
                            startAdornment={<DevicesIcon sx={{ mr: 1 }} />}
                          >
                            <MenuItem value="ALL">Todas as Plataformas</MenuItem>
                            <MenuItem value="android">Android</MenuItem>
                            <MenuItem value="ios">iOS</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Filtro por tipo de plano */}
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Tipo de Plano</InputLabel>
                          <Select
                            value={planTypeFilter}
                            onChange={(e) => setPlanTypeFilter(e.target.value)}
                          >
                            <MenuItem value="ALL">Todos os Planos</MenuItem>
                            <MenuItem value="FREE">Gratuito</MenuItem>
                            <MenuItem value="PREMIUM">Premium</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Filtro por status */}
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                          >
                            <MenuItem value="ALL">Todos os Status</MenuItem>
                            <MenuItem value="active">Ativo</MenuItem>
                            <MenuItem value="pending">Pendente</MenuItem>
                          </Select>
                        </FormControl>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Typography 
                      variant="h6" 
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                    >
                      <PersonIcon color="primary" />
                      Usuários Selecionados
                    </Typography>

                    {specificUsers.length === 0 && (
                      <InfoPaper>
                        <InfoIcon />
                        <Typography variant="body2">
                          Nenhum usuário específico selecionado. Use o botão abaixo para adicionar.
                        </Typography>
                      </InfoPaper>
                    )}

                    {specificUsers.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 1, 
                            maxHeight: 150, 
                            overflow: 'auto',
                            display: 'flex',
                            flexWrap: 'wrap'
                          }}
                        >
                          {specificUsers.map(user => (
                            <UserChip
                              key={user.id}
                              label={user.email}
                              onDelete={() => removeSpecificUser(user.id)}
                              color={hasDeviceToken(user) ? "secondary" : "default"}
                              variant="outlined"
                              icon={!hasDeviceToken(user) ? <WarningIcon /> : undefined}
                            />
                          ))}
                        </Paper>

                        {specificUsers.some(user => !hasDeviceToken(user)) && (
                          <WarningPaper sx={{ mt: 2 }}>
                            <WarningIcon />
                            <Typography variant="body2">
                              Alguns usuários selecionados não têm dispositivos registrados e não receberão notificações.
                            </Typography>
                          </WarningPaper>
                        )}

                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={clearSpecificUsers}
                          startIcon={<ClearIcon />}
                          sx={{ mt: 1 }}
                        >
                          Limpar seleção
                        </Button>
                      </Box>
                    )}
                  </>
                )}

                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="outlined"
                        fullWidth
                        color="primary"
                        onClick={() => setUserSearchDialog(true)}
                        startIcon={<PersonAddIcon />}
                        size="large"
                      >
                        Selecionar Usuários
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        onClick={selectOnlyMe}
                        startIcon={<BugReportIcon />}
                        size="large"
                      >
                        Testar (Apenas Eu)
                      </Button>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Receberão a notificação:
                    </Typography>
                    <Badge 
                      badgeContent={getRecipientsCount()} 
                      color="primary"
                      sx={{ 
                        '& .MuiBadge-badge': { 
                          fontSize: '1rem', 
                          height: 25, 
                          minWidth: 25,
                          borderRadius: '50%'
                        }
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          width: 60, 
                          height: 60, 
                          bgcolor: 'primary.light',
                          mt: 1
                        }}
                      >
                        <DevicesIcon sx={{ fontSize: 32 }} />
                      </Avatar>
                    </Badge>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Slide>

          {/* Novo seletor de tela de destino */}
          <Slide direction="right" in={true} mountOnEnter unmountOnExit style={{ transitionDelay: '50ms' }}>
            {renderDestinationSelector()}
          </Slide>

          <Slide direction="right" in={true} mountOnEnter unmountOnExit style={{ transitionDelay: '100ms' }}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotificationsIcon color="primary" />
                  Detalhes da Notificação
                </Typography>
                
                <TextField
                  label="Título da Notificação"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  placeholder="Ex: Nova atualização disponível!"
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
                
                <TextField
                  label="Corpo da Notificação"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  margin="normal"
                  variant="outlined"
                  placeholder="Ex: Temos novidades para você! Confira as novas funcionalidades..."
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleClearForm}
                    startIcon={<ClearIcon />}
                  >
                    Limpar Formulário
                  </Button>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={previewMode}
                        onChange={(e) => setPreviewMode(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <VisibilityIcon fontSize="small" />
                        <span>Visualizar</span>
                      </Box>
                    }
                  />
                </Box>
              </CardContent>
            </StyledCard>
          </Slide>
        </Grid>

        <Grid item xs={12} md={6}>
          <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Box>
              {previewMode ? (
                <StyledCard sx={{ p: 3, display: 'flex', justifyContent: 'center', flexDirection: 'column', height: '100%' }}>
                  <Typography 
                    variant="h6" 
                    align="center" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: 1,
                      mb: 3
                    }}
                  >
                    <VisibilityIcon color="primary" />
                    Preview da Notificação
                  </Typography>
                  
                  {renderPhonePreview()}
                </StyledCard>
              ) : (
                <StyledCard sx={{ 
                  p: 4, 
                  display: 'flex', 
                  justifyContent: 'center', 
                  flexDirection: 'column', 
                  height: '100%',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)'
                }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <NotificationsIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2, opacity: 0.8 }} />
                    <Typography variant="h5" gutterBottom>
                      Instruções de Envio
                    </Typography>
                    <Typography color="text.secondary">
                      Configure o conteúdo da notificação ao lado e selecione para qual tela deseja direcionar o usuário.
                    </Typography>
                    
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f8f8', borderRadius: 2, textAlign: 'left' }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                        Dicas rápidas:
                      </Typography>
                      <Typography variant="body2" component="div">
                        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                          <li>Use o modo <strong>Todos Filtrados</strong> para enviar para grupos de usuários</li>
                          <li>Selecione uma tela de destino para <strong>direcionar</strong> o usuário após o toque na notificação</li>
                          <li>Para telas de <strong>detalhes</strong>, lembre-se de inserir o ID do item</li>
                          <li>Use o <strong>JSON personalizado</strong> para configurações avançadas</li>
                          <li>Ative a visualização para ver como a notificação irá aparecer</li>
                        </ul>
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setPreviewMode(true)}
                      startIcon={<VisibilityIcon />}
                      sx={{ mb: 2 }}
                    >
                      Ativar Visualização
                    </Button>
                  </Box>
                </StyledCard>
              )}
            </Box>
          </Slide>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }} ref={successRef}>
        <SendButton
          onClick={handleSendNotification}
          disabled={loading || !title || !body || getRecipientsCount() === 0 || (destinationType === 'screen' && needsId() && !itemId)}
          fullWidth
          size="large"
          startIcon={<SendIcon />}
          sx={{ py: 1.8, fontSize: '1.1rem' }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            `Enviar Notificação (${getRecipientsCount()} destinatário${getRecipientsCount() !== 1 ? 's' : ''})`
          )}
        </SendButton>
      </Box>

      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      
      {/* Dialog para busca de usuários específicos */}
      {renderUserSearchDialog()}
    </Container>
  );
};

export default SendNotification;